/**
 * useFinanceSalesReport — Issue #373.
 *
 * Calls the sales management report API via @jinzhuan/api-client.
 * No frontend fake state; all data from backend.
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type SalesReportBatchSummaryDto =
  ApiComponents['schemas']['SalesReportBatchSummaryDto'];
export type SalesReportBatchDetailDto =
  ApiComponents['schemas']['SalesReportBatchDetailDto'];
export type SalesReportBatchListResponseDto =
  ApiComponents['schemas']['SalesReportBatchListResponseDto'];
export type GenerateSalesReportDto =
  ApiComponents['schemas']['GenerateSalesReportDto'];
export type ReviewSalesReportDto =
  ApiComponents['schemas']['ReviewSalesReportDto'];
export type PublishSalesReportDto =
  ApiComponents['schemas']['PublishSalesReportDto'];
export type DataSourceStatusDto =
  ApiComponents['schemas']['DataSourceStatusDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    if (err.status === 401) return { message: '会话已失效', code: 'UNAUTHENTICATED' };
    if (err.status === 403) return { message: '没有权限', code: 'FORBIDDEN' };
    if (err.status === 404) return { message: '记录不存在', code: 'NOT_FOUND' };
    if (err.status === 409) return { message: '数据冲突，请刷新重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// The OpenAPI status enum is mirrored here so the typed `apiClient.GET` call
// below sees a literal union (matching the regenerated schema) instead of a
// loose `string`. Keep these values in sync with the backend enum in
// `apps/api/src/modules/finance-sales-report/dtos/sales-report.dto.ts`.
export type SalesReportBatchStatus =
  | 'draft'
  | 'generated'
  | 'finance_reviewed'
  | 'published'
  | 'archived';

export interface ListSalesReportFilter {
  status?: SalesReportBatchStatus;
  periodYear?: number;
  periodMonth?: number;
  page?: number;
  limit?: number;
}

const SALES_REPORT_BATCH_STATUS: ReadonlySet<SalesReportBatchStatus> = new Set([
  'draft',
  'generated',
  'finance_reviewed',
  'published',
  'archived',
]);

/**
 * Narrow an arbitrary string (e.g. from a `<select>` element) to the typed
 * batch-status union, returning `undefined` for any other value. The page
 * stores the dropdown state as plain `string` (empty string = "all"), so we
 * cannot type the filter input as the union all the way down — but we MUST
 * narrow before forwarding to the typed api-client.
 */
export function coerceBatchStatus(
  raw: string | undefined,
): SalesReportBatchStatus | undefined {
  if (!raw) return undefined;
  return SALES_REPORT_BATCH_STATUS.has(raw as SalesReportBatchStatus)
    ? (raw as SalesReportBatchStatus)
    : undefined;
}

// ─── List / detail ─────────────────────────────────────────────────────────────

export function useSalesReportBatches() {
  const [data, setData] = useState<SalesReportBatchListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter: ListSalesReportFilter = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET('/api/v1/finance/sales-reports', {
        params: {
          query: {
            status: filter.status,
            periodYear: filter.periodYear,
            periodMonth: filter.periodMonth,
            page: filter.page,
            limit: filter.limit,
          },
        },
      });
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useSalesReportBatchDetail() {
  const [data, setData] = useState<SalesReportBatchDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET('/api/v1/finance/sales-reports/{id}', {
        params: { path: { id } },
      });
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
}

// ─── Actions ───────────────────────────────────────────────────────────────────

export function useSalesReportActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(
    async (dto: GenerateSalesReportDto): Promise<SalesReportBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST('/api/v1/finance/sales-reports/generate', {
          body: dto,
        });
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const review = useCallback(
    async (id: string, dto: ReviewSalesReportDto): Promise<SalesReportBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST('/api/v1/finance/sales-reports/{id}/review', {
          params: { path: { id } },
          body: dto,
        });
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const publish = useCallback(
    async (id: string, dto: PublishSalesReportDto): Promise<SalesReportBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST('/api/v1/finance/sales-reports/{id}/publish', {
          params: { path: { id } },
          body: dto,
        });
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, generate, review, publish };
}
